import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { createAdminUser, deleteAdminUser, getAllAdminUsers } from '../../services/adminUserService';

function AdminUserManagement() {
  const { admin } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadAdminUsers();
  }, []);

  const loadAdminUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const admins = await getAllAdminUsers();
      setAdminUsers(admins);
    } catch (err) {
      console.error('Error loading admin users:', err);
      setError('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check if email already exists
    const emailExists = adminUsers.some(admin => 
      admin.email.toLowerCase() === formData.email.toLowerCase()
    );
    if (emailExists) {
      setError('This email is already registered as an admin');
      return;
    }

    try {
      setSaving(true);
      const result = await createAdminUser(
        formData.email,
        formData.password,
        admin.uid,
        admin.email
      );

      if (result.success) {
        setSuccess(result.message);
        setFormData({
          email: '',
          password: '',
          confirmPassword: ''
        });
        setShowAddForm(false);
        await loadAdminUsers(); // Reload list
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error creating admin user:', err);
      setError(err.message || 'Failed to create admin user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAdmin = async (adminUid, adminEmail) => {
    if (!window.confirm(`Are you sure you want to remove admin "${adminEmail}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const result = await deleteAdminUser(
        adminUid,
        adminEmail,
        admin.uid,
        admin.email
      );

      if (result.success) {
        setSuccess(result.message);
        await loadAdminUsers(); // Reload list
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error deleting admin user:', err);
      setError(err.message || 'Failed to delete admin user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <i className="bi bi-shield-check me-2"></i>
                Admin User Management
              </h4>
              <button
                className="btn btn-light btn-sm"
                onClick={() => {
                  setShowAddForm(!showAddForm);
                  setError('');
                  setSuccess('');
                  setFormData({
                    email: '',
                    password: '',
                    confirmPassword: ''
                  });
                }}
              >
                <i className={`bi ${showAddForm ? 'bi-x-lg' : 'bi-plus-lg'} me-2`}></i>
                {showAddForm ? 'Cancel' : 'Add Admin'}
              </button>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError('')}
                  ></button>
                </div>
              )}

              {success && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {success}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSuccess('')}
                  ></button>
                </div>
              )}

              {/* Add Admin Form */}
              {showAddForm && (
                <div className="card mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="card-body">
                    <h5 className="mb-3">
                      <i className="bi bi-person-plus me-2"></i>
                      Add New Admin
                    </h5>
                    <form onSubmit={handleAddAdmin}>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="email" className="form-label">
                            Email Address <span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="admin@example.com"
                            required
                          />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label htmlFor="password" className="form-label">
                            Password <span className="text-danger">*</span>
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            id="password"
                            value={formData.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            placeholder="Enter password"
                            required
                            minLength={6}
                          />
                          <small className="text-muted">Min. 6 characters</small>
                        </div>
                        <div className="col-md-3 mb-3">
                          <label htmlFor="confirmPassword" className="form-label">
                            Confirm Password <span className="text-danger">*</span>
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            id="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                            placeholder="Confirm password"
                            required
                            minLength={6}
                          />
                        </div>
                      </div>
                      <div className="d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-outline-secondary me-2"
                          onClick={() => {
                            setShowAddForm(false);
                            setFormData({
                              email: '',
                              password: '',
                              confirmPassword: ''
                            });
                          }}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Creating...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check-lg me-2"></i>
                              Create Admin
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Admin Users List */}
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Email</th>
                      <th>Created At</th>
                      <th>Created By</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-4">
                          No admin users found
                        </td>
                      </tr>
                    ) : (
                      adminUsers.map((adminUser) => (
                        <tr key={adminUser.uid}>
                          <td>
                            <i className="bi bi-envelope me-2"></i>
                            <strong>{adminUser.email}</strong>
                            {adminUser.uid === admin.uid && (
                              <span className="badge bg-info ms-2">You</span>
                            )}
                          </td>
                          <td>
                            {adminUser.createdAt ? (
                              <>
                                <i className="bi bi-calendar me-2"></i>
                                {adminUser.createdAt.toLocaleDateString()}
                                <br />
                                <small className="text-muted">
                                  {adminUser.createdAt.toLocaleTimeString()}
                                </small>
                              </>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>
                          <td>
                            {adminUser.createdByEmail ? (
                              <span className="text-muted">{adminUser.createdByEmail}</span>
                            ) : (
                              <span className="text-muted">System</span>
                            )}
                          </td>
                          <td>
                            {adminUser.isActive ? (
                              <span className="badge bg-success">
                                <i className="bi bi-check-circle me-1"></i>
                                Active
                              </span>
                            ) : (
                              <span className="badge bg-secondary">
                                <i className="bi bi-x-circle me-1"></i>
                                Inactive
                              </span>
                            )}
                          </td>
                          <td>
                            {adminUser.uid === admin.uid ? (
                              <span className="text-muted small">Cannot delete yourself</span>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteAdmin(adminUser.uid, adminUser.email)}
                                disabled={saving}
                                title="Remove Admin"
                              >
                                <i className="bi bi-trash me-1"></i>
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="alert alert-info mt-3">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Note:</strong> New admins can login using their email and password on the admin login page.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminUserManagement;

